<script setup lang="ts">
  import { computed, getCurrentInstance, ref, shallowRef, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import V2ReservationsNotice from '@/components/v2/reservations/V2ReservationsNotice.vue';
  import type { IReservationFloorPlan } from '@/composition/reservations/use-reservation-floor-plan';
  import type { IReservationTablesDraft } from '@/composition/reservations/use-reservation-tables-draft';
  import {
    effectiveOnlineMaxParty,
    maxBookableOnlineParty,
  } from '@/domain/reservations/reservation-capacity';
  import type { IReservationGroupTableOption } from '@/domain/reservations/reservation-group-tables';
  import {
    TABLE_SEATS_MAX,
    TABLE_SEATS_MIN,
    selectionSummary,
  } from '@/domain/reservations/reservation-tables-draft';
  import type {
    IReservationTableDraftValues,
    TReservationTablesDraftValues,
  } from '@/domain/reservations/reservation-tables-draft';
  import { isOnlineReservationTable } from '@/domain/reservations/reservation-tables';
  import type { IReservationTableRef } from '@/interfaces/IReservation';
  import type {
    IReservationSettingsConfiguration,
    TTableReservationsMode,
  } from '@/interfaces/IReservationSettings';
  import V2ReservationTableSelectionBar from './V2ReservationTableSelectionBar.vue';
  import V2ReservationTablesPlan from './V2ReservationTablesPlan.vue';

  /**
   * Seats and bookability editor shared by the setup guide (toggle mode
   * control, manual save) and the Ajustes tables card (three-way mode
   * control, autosave). Every edit goes to the draft overlay it is given.
   */
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    draft: IReservationTablesDraft;
    floorPlan: IReservationFloorPlan;
    modeControl: 'toggle' | 'segmented';
    allSeats: number;
    /** Tables the guide's name heuristic set aside (D12); listed in an undoable note. */
    excludedIds?: number[];
    disabled?: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'update:allSeats', value: number): void;
    (e: 'undo-exclusions'): void;
    (e: 'touched', ids: number[]): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const EXCLUDED_NAMES_SHOWN = 5;

  const activeSectorId = ref<number | null>(null);
  const selectedIds = ref<number[]>([]);
  /**
   * What the affected tables showed before «Poner en todas», kept while
   * «Deshacer» is offered. Written back as overlay entries (not as a raw
   * overlay swap), so an autosaving card also writes the revert.
   */
  const applyAllUndo = shallowRef<TReservationTablesDraftValues | null>(null);
  /** What «Poner en todas» wrote, so the line stays true if the stepper moves. */
  const applyAllSeats = ref(0);
  const applyAllCount = ref(0);

  const tables = computed(() => props.draft.tables.value);
  const byId = computed(
    () => new Map(tables.value.map((table) => [table.id, table]))
  );
  const selection = computed(() =>
    selectedIds.value
      .map((id) => byId.value.get(id))
      .filter((table): table is IReservationTableRef => table !== undefined)
  );
  const scopeKey = computed(
    () =>
      `${props.configuration.settings.restaurantId}:${props.configuration.settings.branchId}`
  );
  const excluded = computed(() =>
    (props.excludedIds ?? [])
      .map((id) => byId.value.get(id))
      .filter((table): table is IReservationTableRef => table !== undefined)
  );
  const excludedText = computed(() => {
    const names = excluded.value.map((table) => table.name);
    const shown = names.slice(0, EXCLUDED_NAMES_SHOWN).join(', ');
    const rest = names.length - EXCLUDED_NAMES_SHOWN;
    const list =
      rest > 0
        ? `${shown} ${tr('reservations.tables.editor.excludedMore', {
            count: rest,
          })}`
        : shown;
    return String(
      proxy.$tc('reservations.tables.editor.excluded', names.length, {
        names: list,
      })
    );
  });
  const onlineCount = computed(
    () =>
      tables.value.filter(
        (table) => isOnlineReservationTable(table) && table.capacity > 0
      ).length
  );
  const onlineMax = computed(() =>
    effectiveOnlineMaxParty(
      props.configuration.settings.minPartyOnline,
      props.configuration.settings.maxPartyOnline,
      maxBookableOnlineParty(tables.value, props.configuration.tableGroups)
    )
  );

  function selectable(table: IReservationGroupTableOption) {
    return table.enabled;
  }
  function clearSelection() {
    selectedIds.value = [];
  }
  function toggle(id: number) {
    if (props.disabled) return;
    selectedIds.value = selectedIds.value.includes(id)
      ? selectedIds.value.filter((value) => value !== id)
      : [...selectedIds.value, id];
  }
  /** Any edit ends the «Poner en todas» undo offer. */
  function edited(ids: number[]) {
    applyAllUndo.value = null;
    emit('touched', ids);
  }
  function adjust(delta: 1 | -1) {
    const ids = [...selectedIds.value];
    if (!ids.length) return;
    props.draft.adjustSeats(ids, delta);
    edited(ids);
  }
  /** Off → online keeping seats (or the global value); online → staff only. */
  function toggleOff() {
    const ids = [...selectedIds.value];
    if (!ids.length) return;
    if (selectionSummary(selection.value).allOff)
      props.draft.setMode(ids, 'online', props.allSeats);
    else props.draft.setMode(ids, 'internal');
    edited(ids);
  }
  function setMode(mode: TTableReservationsMode) {
    const ids = [...selectedIds.value];
    if (!ids.length) return;
    props.draft.setMode(ids, mode, props.allSeats);
    edited(ids);
  }
  function applyAll() {
    const affected = tables.value.filter((table) =>
      isOnlineReservationTable(table)
    );
    if (!affected.length) return;
    const snapshot: Record<number, IReservationTableDraftValues> = {};
    for (const table of affected)
      snapshot[table.id] = {
        capacity: table.capacity,
        reservationsMode: table.reservationsMode,
      };
    props.draft.applyToAll(props.allSeats);
    applyAllUndo.value = snapshot;
    applyAllSeats.value = props.allSeats;
    applyAllCount.value = affected.length;
    emit(
      'touched',
      affected.map((table) => table.id)
    );
  }
  function undoApplyAll() {
    if (applyAllUndo.value === null) return;
    props.draft.restore({ ...props.draft.values.value, ...applyAllUndo.value });
    applyAllUndo.value = null;
  }
  function undoExclusions() {
    // During the in-flight PUT the undone tables would stay dirty forever
    // (adoptSaved only adopts entries left untouched).
    if (props.disabled) return;
    const ids = [...(props.excludedIds ?? [])];
    props.draft.setMode(ids, 'online', props.allSeats);
    applyAllUndo.value = null;
    emit('undo-exclusions');
  }

  watch(scopeKey, () => {
    clearSelection();
    activeSectorId.value = null;
    applyAllUndo.value = null;
  });
  // A selected table that disappears (deleted elsewhere) leaves the selection.
  watch(tables, () => {
    const kept = selectedIds.value.filter((id) => byId.value.has(id));
    if (kept.length !== selectedIds.value.length) selectedIds.value = kept;
  });
</script>

<template>
  <div class="reservation-tables-editor">
    <div class="all-tables">
      <div class="all-tables-row">
        <span class="all-tables-label">{{
          $t('reservations.tables.editor.all')
        }}</span>
        <!-- Stepper and unit wrap as one, so phones only drop the button to the next line. -->
        <span class="all-tables-seats">
          <PosUiStepper
            :value="allSeats"
            :min="TABLE_SEATS_MIN"
            :max="TABLE_SEATS_MAX"
            size="md"
            editable
            :disabled="disabled"
            :aria-label="$t('reservations.tables.editor.all')"
            @input="emit('update:allSeats', $event)"
          />
          <span class="all-tables-unit">{{
            $t('reservations.tables.editor.people')
          }}</span>
        </span>
        <PosUiButton
          class="apply-all"
          color="neutral"
          variant="outline"
          :disabled="disabled"
          @click="applyAll"
          >{{ $t('reservations.tables.editor.applyAll') }}</PosUiButton
        >
      </div>
      <p class="all-tables-hint">
        {{ $t('reservations.tables.editor.applyHint') }}
      </p>
      <p v-if="applyAllUndo !== null" class="applied-line" role="status">
        <span>{{
          $tc('reservations.tables.editor.applied', applyAllCount, {
            seats: applyAllSeats,
            count: applyAllCount,
          })
        }}</span>
        <button
          type="button"
          class="undo-link"
          :disabled="disabled"
          @click="undoApplyAll"
        >
          {{ $t('reservations.tables.editor.undo') }}
        </button>
      </p>
    </div>
    <V2ReservationTablesPlan
      :areas="floorPlan.areas.value"
      :unplaced="floorPlan.unplaced.value"
      :active-sector-id="activeSectorId ?? undefined"
      :selected-ids="selectedIds"
      mode="seats"
      :variant="modeControl === 'toggle' ? 'guide' : 'settings'"
      :selectable="selectable"
      :disabled="disabled"
      :status="floorPlan.status.value"
      @toggle="toggle"
      @area="activeSectorId = $event"
      @retry="floorPlan.load()"
    />
    <V2ReservationTableSelectionBar
      v-if="selection.length"
      :selection="selection"
      :mode-control="modeControl"
      mode="seats"
      :disabled="disabled"
      @adjust="adjust"
      @toggle-off="toggleOff"
      @mode="setMode"
      @done="clearSelection"
    />
    <V2ReservationsNotice
      v-if="excluded.length"
      class="excluded-note"
      icon="information-outline"
      :text="excludedText"
      :action-label="$t('reservations.tables.editor.undo')"
      :disabled="disabled"
      @action="undoExclusions"
    />
    <p class="counter-line">
      <span
        ><strong>{{ onlineCount }}</strong>
        {{ $tc('reservations.tables.editor.counterTables', onlineCount) }}</span
      >
      <template v-if="onlineCount > 0">
        <span class="counter-separator" aria-hidden="true">·</span>
        <i18n path="reservations.tables.editor.counterMax" tag="span">
          <template #max
            ><strong>{{ onlineMax }}</strong></template
          >
        </i18n>
      </template>
    </p>
  </div>
</template>

<style scoped lang="scss">
  .reservation-tables-editor {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-4;
    // The global row: one number for every online table, then per-table taps.
    .all-tables {
      @apply tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-4 tw-py-3;
    }
    .all-tables-row {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .all-tables-label {
      @apply tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .all-tables-seats {
      @apply tw-flex tw-items-center tw-gap-2;
    }
    .all-tables-unit {
      @apply tw-text-sm tw-text-neutral-600;
    }
    .all-tables-hint {
      @apply tw-mb-0 tw-mt-2 tw-text-xs tw-text-neutral-500;
    }
    .applied-line {
      @apply tw-mb-0 tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-sm tw-text-amber-700;
    }
    .undo-link {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-amber-800 tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }
    }
    .excluded-note {
      @apply tw-mb-0;
    }
    .counter-line {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-sm tw-text-neutral-600;
      strong {
        @apply tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      }
      .counter-separator {
        @apply tw-text-neutral-400;
      }
    }
  }
</style>
